<template>
  <div
    class="min-h-[100dvh] max-h-[100dvh] font-inter bg-pitch-black text-total-white flex flex-col justify-between"
  >
    <TopNavbar
      :isThread="true"
      :userRole="userRole"
      :projectId="project?.id"
      :isSimulation="project?.is_simulation"
      :projectName="project?.address_1"
    />

    <!-- Header Section -->
    <div
      class="flex items-center justify-between px-4 bg-eerie-black py-2 border-b border-light-grey-translucent-dark"
    >
      <div class="flex items-center space-x-2">
        <button @click="goBack" class="text-prime-golden">
          <img src="../../assets/images/left-chevron.svg" class="fas w-3 fa-arrow-left" />
        </button>
      </div>
      <div>
        <!-- Dynamic participant names from to_contact_stakeholders -->
        <h2 class="text-lg font-bold">
          <template v-if="participants.length > 0">
            <router-link
              v-for="participant in participants"
              :key="participant.id"
              :to="{
                name: 'ContactViewPage',
                params: { id: participant.id },
                query: { from_text: true, threadId: threadId, projectId: projectId },
              }"
            >
              <span>{{ getFirstNameOrNumber(participant) }}</span
              >{{ participant !== participants[participants.length - 1] ? ", " : "" }}
            </router-link>
          </template>
          <template v-else> ... </template>
        </h2>
        <!-- Dynamic project address -->
        <p class="text-sm text-total-white font-semibold">{{ project?.address_1 }}</p>
      </div>
      <button class="text-prime-golden" @click="prepareTextThreadForDeletion()">
        <img src="../../assets/images/trash-icon.svg" class="w-5 h-5" />
      </button>
    </div>

    <!-- Progress Bar -->
    <div
      v-if="progressBarWidth != '0%'"
      class="h-1 bg-prime-golden transition-all duration-300 w-full"
      :style="{ width: progressBarWidth }"
    ></div>

    <!-- Chat Messages Section -->
    <div
      ref="messagesContainer"
      class="overflow-y-auto flex-1 h-full px-4 py-4 pt-6 space-y-4"
      @scroll.passive="handleScroll"
    >
      <TextBubble
        v-for="(msg, index) in mappedMessages"
        :key="index"
        :side="msg.side"
        :avatar="msg.avatar ? msg.avatar : ''"
        :message="msg.message"
        :timestamp="msg.timestamp"
        :image="msg.image"
        :files="msg.files"
      />
    </div>

    <!-- Message Input Section -->
    <div
      v-if="canReply"
      class="flex flex-wrap items-center px-3 py-2 mt-auto bg-eerie-black border-t border-dark-grey max-h-80"
      :class="{ 'opacity-40': isMessageSending, 'pointer-events-none': isMessageSending }"
    >
      <!-- Attachments Preview -->
      <div
        v-if="attachments.length > 0"
        class="py-2 mb-1 flex flex-wrap justify-start items-center gap-4 w-full"
      >
        <h3 class="text-sm font-semibold text-total-white">Attachments:</h3>
        <div class="flex flex-wrap gap-2">
          <div
            v-for="(file, index) in attachments"
            :key="index"
            class="text-md text-prime-golden bg-dark-grey px-2 py-1 rounded-lg flex items-center"
          >
            {{ file.name }}
            <button @click="removeAttachment(index)" class="ml-2 text-prime-golden">
              ×
            </button>
          </div>
        </div>
      </div>

      <!-- Attachments Button -->
      <label
        for="fileInput"
        class="text-total-white-500 w-8 h-8 flex items-center justify-center bg-light-grey-translucent px-1 rounded-full mr-3"
      >
        <i class="fas fa-plus text-base"></i>
      </label>
      <input
        type="file"
        id="fileInput"
        class="hidden"
        @change="handleFileUpload"
        multiple
      />

      <!-- Message Input and Send Button -->
      <div
        class="bg-pitch-black text-total-white rounded-full flex items-center px-2 border border-ash-grey flex-1 focus:rounded-xl"
        :class="{ 'rounded-xl': newMessageFocus }"
      >
        <textarea
          v-model="newMessage"
          placeholder="Enter Message..."
          @keydown.enter="sendMessage"
          :disabled="!canReply"
          @focus="newMessageFocus = true"
          class="flex-1 bg-pitch-black text-total-white py-1 px-2 rounded-full focus:outline-none resize-none"
          :class="{ 'h-20 py-4 rounded-sm': newMessageFocus }"
          :rows="newMessage ? newMessage.split('\n').length : 1"
        ></textarea>
        <button
          @click="sendMessage"
          class="bg-prime-golden text-pitch-black px-[0.4rem] rounded-full ml-2"
          :disabled="!canSendMessage || isMessageSending || !canReply"
          :class="{
            'opacity-50 pointer-events-none':
              !canSendMessage || isMessageSending || !canReply,
          }"
        >
          <i class="fas fa-arrow-up text-sm"></i>
        </button>
      </div>

      <!-- Delete Modal (if applicable) -->
      <DeleteModal
        :isOpen="showDeleteModal"
        :heading="deletionHeading"
        :message="deletionMessage"
        @confirmDelete="deleteTextThreadOrMessage"
        @cancelDelete="showDeleteModal = false"
      />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import { useRoute } from "vue-router";
import { useLogStore } from "@/stores/log";
import TextBubble from "@/components/TextBubble.vue";
import TopNavbar from "@/components/TopNavbar.vue";
import debounce from "lodash/debounce";
import { useProjectStore } from "@/stores/project";
import router from "@/router";
import DeleteModal from "@/components/DeleteModal.vue";
import { convertHeicToPng } from "@/misc/util";
import { useUserStore } from "@/stores/user";

export default {
  components: { TextBubble, TopNavbar, DeleteModal },
  setup() {
    const route = useRoute();
    const threadId = route.params.threadId;
    const projectId = route.params.projectId;
    const logStore = useLogStore();
    const newMessage = ref("");
    const messagesContainer = ref(null);
    const isPolling = ref(true);
    const attachments = ref([]);
    const toPhoneNumbers = ref([]); // Dynamic phone numbers
    const projectStore = useProjectStore(); // Instantiate the store correctly

    const showDeleteModal = ref(false);
    const deletionMessage = ref("");
    const deletionHeading = ref("");
    const deletionType = ref("");
    const textIdToDelete = ref(null);

    const newMessageFocus = ref(false);

    const progressBarWidth = ref("0%");

    const projectManagerTwilioNumber = ref(
      projectStore.currentProject?.project_manager_twilio_number?.phone_number || null
    );
    const userStore = useUserStore();
    const userRole = computed(() => userStore.profile?.account_type);
    const isMessageSending = ref(false);

    // Fetch the project based on the projectId from the route
    const project = computed(() => projectStore.currentProject);

    // Fetch participants from the messages
    const participants = computed(() => {
      if (logStore.stakeholders.length === 0) return []; // If stakeholders are not yet fetched, return empty array.

      if (logStore.threadMessages.length === 0) return [];

      if (logStore.threadMessages[0].thread_id != threadId) return [];

      const toParticipants = logStore.threadMessages[0]?.to_contact_stakeholders?.map(
        (stakeholder) => ({
          first_name: stakeholder.first_name,
          id: stakeholder.id,
          phone: stakeholder.phone,
          role: stakeholder.role,
        })
      );
      const fromParticipant = logStore.threadMessages[0]?.from_contact_stakeholder
        ? {
            first_name: logStore.threadMessages[0]?.from_contact_stakeholder.first_name,
            id: logStore.threadMessages[0]?.from_contact_stakeholder.id,
            phone: logStore.threadMessages[0]?.from_contact_stakeholder.phone,
            role: logStore.threadMessages[0]?.from_contact_stakeholder.role,
          }
        : null;

      return toParticipants.concat(fromParticipant).filter(Boolean).reverse(); // Reverse and filter out null values
    });
    const participantMapping = computed(() => {
      if (!logStore.threadMessages.length) return {};
      // if (logStore.threadMessages[0].thread_id !== threadId) return {};

      const toParticipants =
        logStore.threadMessages[0]?.to_contact_stakeholders?.map((st) => ({
          first_name: st.first_name,
          id: st.id,
          phone: st.phone,
          role: st.role,
        })) || [];
      const fromParticipant = logStore.threadMessages[0]?.from_contact_stakeholder
        ? {
            first_name: logStore.threadMessages[0].from_contact_stakeholder.first_name,
            id: logStore.threadMessages[0].from_contact_stakeholder.id,
            phone: logStore.threadMessages[0].from_contact_stakeholder.phone,
            role: logStore.threadMessages[0].from_contact_stakeholder.role,
          }
        : null;

      // Combine participants. (If you previously reversed the order, remove that.)
      const parts = fromParticipant
        ? toParticipants.concat(fromParticipant)
        : toParticipants;

      let rightParticipant = null;

      // Rule 1: If a Project Manager (PM/self) is present, choose that.
      rightParticipant = parts.find((p) => p.role === "Project Manager");

      // Rule 2: Otherwise, if a Project Agent is present, choose that.
      if (!rightParticipant) {
        rightParticipant = parts.find((p) => p.role === "Project Agent");
      }

      // Rule 3: Otherwise, if exactly two participants exist, choose one alphabetically.
      if (!rightParticipant && parts.length === 2) {
        rightParticipant = parts
          .slice()
          .sort((a, b) => a.first_name.localeCompare(b.first_name))[0];
      }

      // Rule 4: Otherwise, if more than two exist, choose the alphabetically first.
      if (!rightParticipant && parts.length > 0) {
        rightParticipant = parts
          .slice()
          .sort((a, b) => a.first_name.localeCompare(b.first_name))[0];
      }

      // Build mapping: assign "right" to the chosen participant and "left" to all others.
      const mapping = {};
      if (rightParticipant) {
        mapping[rightParticipant.id] = "right";
        parts.forEach((p) => {
          if (p.id !== rightParticipant.id) {
            mapping[p.id] = "left";
          }
        });
      }
      return mapping;
    });

    const canReply = computed(() => {
      return participants.value.some(
        (participant) => participant.role === "Project Manager"
      );
    });

    const getFirstNameOrNumber = (participant) => {
      if (participant?.first_name == "" || participant?.first_name == "Unknown") {
        return participant?.phone;
      }
      return participant?.first_name;
    };

    // Fetch initial messages if threadId exists
    const fetchInitialMessages = async () => {
      if (threadId) {
        await logStore.fetchMessagesByThreadId(threadId, "asc", 1);
        updatePhoneNumbers(); // Update phone numbers after fetching messages
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop += 1000; // Scroll to bottom after each poll
        }
      }
    };

    const handleFileUpload = (event) => {
      const files = Array.from(event.target.files);
      attachments.value.push(...files);
    };

    const goBack = () => {
      if (route.query.from_contact) {
        // router.go(-1);
        router.push({
          name: "ProjectPage",
          params: { id: route.query.projectId || route.params.projectId },
          query: { tab: "log" },
        });
      } else if (route.query.tab) {
        router.push({
          name: "ProjectPage",
          params: { id: route.query.projectId || route.params.projectId },
          query: {
            tab: route.query.tab || route.query.currentTab,
            readLogs: route.query.readLogs,
          },
        });
      } else if (route.query.projectId) {
        router.push({
          name: "ProjectPage",
          params: { id: route.query.projectId || route.params.projectId },
          query: { tab: route.query.tab || route.query.currentTab || "contacts" },
        });
      } else {
        router.push({
          name: "ContactBookPage",
        });
      }
    };

    const removeAttachment = (index) => {
      attachments.value.splice(index, 1);
    };

    // Computed property to map the messages for display
    const mappedMessages = computed(() => {
      if (
        logStore.threadMessages.length > 0 &&
        logStore.threadMessages[0].thread_id != threadId
      ) {
        return [];
      }
      const mapping = participantMapping.value;
      return logStore.threadMessages.map((msg) => {
        let side = "left"; // default
        if (msg.from_contact_stakeholder && mapping[msg.from_contact_stakeholder.id]) {
          side = mapping[msg.from_contact_stakeholder.id];
        }
        const allMedia = msg.media_urls || [];

        const imageUrl =
          allMedia.find((url) => /\.(jpe?g|png|gif|svg|heic|heif)$/i.test(url)) || null;

        const files = allMedia
          .filter((url) => !/\.(jpe?g|png|gif|svg|heic|heif)$/i.test(url))
          .map((url) => {
            const name = url.split("/").pop().split("?")[0];
            let type = "";
            if (url.match(/\.pdf$/i)) {
              type = "application/pdf";
            } else if (url.match(/\.xlsx?$/i)) {
              type = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
            }
            return { file_url: url, file_name: name, file_type: type };
          });

        return {
          id: msg.id,
          side, // will be "left" or "right"
          message: msg.content,
          avatar: msg.from_contact_stakeholder?.profile_image_url,
          timestamp: new Date(msg.date_time_created).toLocaleString(),
          image: imageUrl,
          files,
        };
      });
    });

    // Function to update phone numbers dynamically from to_contact_stakeholders
    const updatePhoneNumbers = () => {
      toPhoneNumbers.value = logStore.threadMessages.flatMap((msg) =>
        msg.to_contact_stakeholders.map((stakeholder) => stakeholder.phone)
      );
    };
    onBeforeUnmount(() => {
      logStore.clearData(); // This should clear threadMessages and related properties.
    });
    // Poll for new messages every 3 seconds if threadId exists
    const startPolling = () => {
      if (!threadId) return;

      const pollingInterval = setInterval(async () => {
        if (isPolling.value && threadId) {
          const oldNumberOfMessages = logStore.threadMessages.length;
          await logStore.fetchMessagesByThreadId(threadId, "asc", 1);
          updatePhoneNumbers(); // Update phone numbers on each poll
          if (
            messagesContainer.value &&
            oldNumberOfMessages < logStore.threadMessages.length
          ) {
            messagesContainer.value.scrollTop += 1000; // Scroll to bottom after each poll
          }
        }
      }, 5000);

      onBeforeUnmount(() => clearInterval(pollingInterval));
    };

    // Infinite scroll for older messages if threadId exists
    const handleScroll = debounce(async () => {
      /*if (threadId && messagesContainer.value.scrollTop === 0 && !logStore.isLoading) {
        await logStore.fetchMessagesByThreadId(threadId, "asc", logStore.page + 1);
      }*/
    }, 300);

    const encodeFileToBytes = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const adjustedFileName =
            file.type === "image/heic"
              ? file.name.replace(/\.heic$/i, ".png")
              : file.name; // Change .heic to .png if type is heic
          const blob = new Blob([reader.result], {
            type: file.type === "image/heic" ? "image/png" : file.type,
            name: adjustedFileName,
          }); // Create a Blob from the ArrayBuffer and set the file name
          resolve(blob); // Resolve with the Blob
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file); // Read the file as an array buffer
      });
    };
    const canSendMessage = computed(() => {
      return newMessage.value.trim() !== "" || attachments.value.length > 0;
    });

    // Send a new message
    const sendMessage = async () => {
      if (!canSendMessage.value) return;

      progressBarWidth.value = "20%";

      isMessageSending.value = true;

      const formData = new FormData();
      formData.append("project_id", projectId);
      formData.append("message", newMessage.value);

      if (threadId) formData.append("thread_id", threadId);

      // Encode each file to base64 and append to FormData
      for (const file of attachments.value) {
        let fileAsBytes;

        if (file.type === "image/heic" || file.type === "image/heif") {
          const pngBlob = await convertHeicToPng(file); // Convert HEIC to PNG
          fileAsBytes = await encodeFileToBytes(pngBlob);
        } else {
          fileAsBytes = await encodeFileToBytes(file);
        }

        fileAsBytes.name =
          file.type === "image/heic" || file.type === "image/heif"
            ? file.name.replace(/\.(heic|heif)$/i, ".png")
            : file.name; // Set the file name in the Blob, changing .heic and .heif to .png
        formData.append("files", fileAsBytes, fileAsBytes.name.replace(/\s+/g, "_")); // Append the Blob with the file name
      }

      progressBarWidth.value = "70%";

      try {
        await logStore.sendTextMessage(formData);
        progressBarWidth.value = "100%";
        newMessage.value = "";
        attachments.value = [];
        newMessageFocus.value = false;
        isMessageSending.value = false;
        await new Promise((resolve) => setTimeout(resolve, 1000));
        progressBarWidth.value = "0%";
      } catch (error) {
        console.error("Error sending message:", error);
      }
    };

    // Fetch projects logic
    const fetchProject = async () => {
      if (!projectStore.currentProject) {
        try {
          await projectStore.fetchProjectById(projectId);
        } catch (error) {
          console.error("Error fetching projects:", error);
        }
      }
    };

    const deleteThread = async () => {
      await logStore.deleteTextThread(threadId);
      goBack();
    };

    const deleteTextMessage = async (messageId) => {
      await logStore.deleteTextLog(messageId);
      showDeleteModal.value = false;
    };

    const deleteTextThreadOrMessage = () => {
      if (deletionType.value === "thread") {
        deleteThread();
      } else {
        deleteTextMessage(textIdToDelete.value);
      }
    };

    const prepareTextThreadForDeletion = () => {
      deletionType.value = "thread";
      deletionHeading.value = "Delete Thread";
      deletionMessage.value =
        "Are you sure you want to delete this thread? This operation might be irreversible.";
      showDeleteModal.value = true;
    };

    const prepareTextForDeletion = (messageId) => {
      textIdToDelete.value = messageId;
      deletionType.value = "message";
      deletionHeading.value = "Delete Message";
      deletionMessage.value =
        "Are you sure you want to delete this message? This operation might be irreversible.";
      showDeleteModal.value = true;
    };

    onMounted(() => {
      fetchProject();
      if (threadId) {
        fetchInitialMessages();
        startPolling();
      }
    });
    return {
      projectId,
      threadId,
      mappedMessages,
      newMessage,
      messagesContainer,
      handleScroll,
      attachments,
      participants,
      project,
      sendMessage,
      canSendMessage,
      goBack,
      handleFileUpload,
      removeAttachment,
      canReply,
      deleteThread,
      deleteTextMessage,
      showDeleteModal,
      deletionMessage,
      deletionHeading,
      prepareTextThreadForDeletion,
      prepareTextForDeletion,
      deleteTextThreadOrMessage,
      userRole,
      isMessageSending,
      progressBarWidth,
      getFirstNameOrNumber,
    };
  },
};
</script>

<style scoped>
/* Additional styling for the texting page */
</style>
